
import {
  defineComponent, onBeforeMount, onMounted, onUnmounted, ref, watch,
} from 'vue';
import Checkbox from 'primevue/checkbox';
import { useI18n } from 'vue-i18n';

import {
  getIsAuthenticated, Login, Me, setPermissionsString, SwitchActiveShop, UserState,
} from '@/composables/User';
import { useStore } from '@/store';
import { showToastError } from '@/helpers';
import { NavigationFailure, useRouter } from 'vue-router';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import { getConfigurationKey } from '@/composables/configs/configuration';

export default defineComponent({
  name: 'Login',
  components: {
    BaseTextInput,
    Checkbox,
    SpmButton,
  },

  setup() {
    const checked = ref(false);
    const email = ref('');
    const password = ref('');
    const isEmailNotValid = ref(false);
    const isPasswordNotValid = ref(false);
    const store = useStore();
    const { t } = useI18n();
    const router = useRouter();

    const isLoadingForm = ref(false);

    const checkForm = async (): Promise<void|NavigationFailure|undefined|null> => {
      isLoadingForm.value = true;
      isEmailNotValid.value = !email.value;
      isPasswordNotValid.value = !password.value;

      if (isEmailNotValid.value || isPasswordNotValid.value) {
        isLoadingForm.value = false;
        return null;
      }

      try {
        await Login({ username: email.value, password: password.value }, checked.value);
        await Me(false);

        if (UserState.user.shops.length === 0) {
          isLoadingForm.value = false;
          return showToastError(t('user.noActiveShop'));
        }

        if (UserState.user.shops.length === 1) {
          await SwitchActiveShop(UserState.user.shops[0].id);
          store.commit('liveEditor/resetWebsiteContents');
          isLoadingForm.value = false;
          return router.push({ name: 'home' });
        }

        isLoadingForm.value = false;
        return router.push({ name: 'choose-shop' });
      } catch (error: any) {
        isLoadingForm.value = false;
        if (error?.message === 'no_active_shop') {
          return showToastError(t('user.noActiveShop'));
        }
        return showToastError(t('user.wrongCredentials'));
      }
    };

    watch(email, (value: string) => {
      isEmailNotValid.value = !value;
    });

    watch(password, (value: string) => {
      isPasswordNotValid.value = !value;
    });

    onMounted(() => {
      document.body.classList.add('no_expand');
    });

    onBeforeMount(async () => {
      const isAuthenticated = getIsAuthenticated();
      if (isAuthenticated) {
        return router.push({ name: 'home' });
      }
      document.body.classList.add('no_expand');
      return true;
    });

    onUnmounted(() => {
      document.body.classList.remove('no_expand');
    });

    return {
      checked,
      email,
      password,
      isLoadingForm,
      t,
      checkForm,
    };
  },
});
